import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Loader2, RotateCw, Wallet } from 'lucide-react';
import { type GbuZeit, ladeGbuZeiten } from '../../../services/sifa/gbuService';
import { formatDauer } from '../../../services/sifa/taetigkeitenLogic';
import { describeError } from '../../../services/errorMessage';
import { formatTag, gbuZeitStand, kannNachbuchen, zeitenSumme } from './gbuModulLogic';

/** Eine erfasste Zeit dieser GBU — genau das, was gbuService.ladeGbuZeiten liefert. */
export type GbuZeitZeile = GbuZeit;

interface Props {
  gbuId: string;
  /** Zaehler des Containers: steigt nach jedem Erfassen/Abrechnen — dann neu laden. */
  stand: number;
  isReadOnly: boolean;
  /** Passende LV-Position vorhanden und Schreibweg ins Projekt da. */
  kannAbrechnen: boolean;
  /** Eine Buchung laeuft gerade (Riegel im Container). */
  busy: boolean;
  onAbrechnen: (z: GbuZeitZeile) => void;
  /** Protokoll des Projekts — steht dort `gbu-zeit-<id>`, ist die Gutschrift da. */
  serviceLog: ReadonlyArray<{ id?: string | null }>;
  /** Nicht Nur-Ansicht und Schreibweg ins Projekt vorhanden. */
  kannGutschreiben: boolean;
  onGutschriftNachholen: (z: GbuZeitZeile) => void;
}

/**
 * „Erfasste Zeiten" einer GBU: die Zeilen des Taetigkeitsnachweises mit
 * `gbu_id` — Datum, Dauer, Text, abgerechnet ja/nein. Eine noch nicht
 * abgerechnete Zeile laesst sich hier nachtraeglich abrechnen: derselbe Weg
 * ab dem Abrechnungsdialog (Riegel mit genau DIESER Id, Gutschrift nur bei
 * markierter Id) — es entsteht keine zweite Taetigkeit.
 *
 * Steht der Riegel, fehlt aber die Gutschrift im Projekt (Antwort der RPC
 * verloren, Gutschrift danach gescheitert), zeigt die Zeile „Gutschrift
 * fehlt" (gbuZeitStand) und bietet „Gutschrift nachholen" — OHNE erneuten
 * Riegel, mit Position und Menge aus dem Riegel.
 */
export const GbuZeiten: React.FC<Props> = ({
  gbuId, stand, isReadOnly, kannAbrechnen, busy, onAbrechnen, serviceLog, kannGutschreiben, onGutschriftNachholen,
}) => {
  const [zeiten, setZeiten] = useState<GbuZeitZeile[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    ladeGbuZeiten(gbuId)
      .then((z) => { if (aktiv) { setZeiten(z); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(describeError(e)); });
    return () => { aktiv = false; };
  }, [gbuId, stand]);

  const summe = zeiten ? zeitenSumme(zeiten) : null;
  const fehlend = zeiten ? zeiten.filter((z) => gbuZeitStand(z, serviceLog) === 'gutschrift_fehlt').length : 0;

  return (
    <section className={`bg-white border rounded-2xl p-4 space-y-2 ${fehlend ? 'border-red-300' : 'border-slate-200'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Clock className="w-4 h-4 text-indigo-600" /> Erfasste Zeiten</p>
        {summe && zeiten && zeiten.length > 0 && (
          <p className="text-xs text-slate-500">
            {formatDauer(summe.minuten)} gesamt
            {summe.offen > 0 && <span className="text-amber-700 font-bold"> · {summe.offen} nicht abgerechnet ({formatDauer(summe.offenMinuten)})</span>}
            {fehlend > 0 && <span className="text-red-700 font-bold"> · {fehlend} Gutschrift fehlt</span>}
          </p>
        )}
      </div>

      {fehler ? (
        <p className="text-xs text-red-700 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> {fehler}</p>
      ) : zeiten === null ? (
        <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
      ) : zeiten.length === 0 ? (
        <p className="text-xs text-slate-500">Noch keine Zeit erfasst. „Zeit buchen" oben trägt sie in den Tätigkeitsnachweis ein.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {zeiten.map((z) => {
            const zustand = gbuZeitStand(z, serviceLog);
            return (
              <li key={z.id} className={`py-2 flex flex-wrap items-start gap-2 ${zustand === 'gutschrift_fehlt' ? 'bg-red-50 -mx-2 px-2 rounded-lg' : ''}`}>
                <span className="w-20 shrink-0 text-xs font-bold text-slate-700">{formatTag(z.datum)}</span>
                <span className="w-20 shrink-0 text-xs text-slate-600">{formatDauer(z.dauerMinuten)}</span>
                <span className="flex-1 min-w-[10rem] text-xs text-slate-700">
                  {z.beschreibung || z.titel}
                  {z.durchgefuehrtVon && <span className="text-slate-400"> · {z.durchgefuehrtVon}</span>}
                  {zustand === 'gutschrift_fehlt' && (
                    <span className="block text-[11px] text-red-700 font-bold mt-0.5">
                      Als abgerechnet vermerkt, aber nicht im Leistungsstand des Projekts. Nicht von Hand nachbuchen — „Gutschrift nachholen" bucht genau einmal.
                    </span>
                  )}
                </span>
                {zustand === 'gebucht' ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 whitespace-nowrap">
                    <CheckCircle2 className="w-3.5 h-3.5" /> abgerechnet{z.gebuchteMenge ? ` (${Number(z.gebuchteMenge).toLocaleString('de-DE')})` : ''}
                  </span>
                ) : zustand === 'gutschrift_fehlt' ? (
                  kannGutschreiben ? (
                    <button onClick={() => onGutschriftNachholen(z)} disabled={busy} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-40">
                      <RotateCw className="w-3.5 h-3.5" /> Gutschrift nachholen
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 whitespace-nowrap"><AlertTriangle className="w-3.5 h-3.5" /> Gutschrift fehlt</span>
                  )
                ) : kannNachbuchen(z, isReadOnly, kannAbrechnen) ? (
                  <button onClick={() => onAbrechnen(z)} disabled={busy} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg disabled:opacity-40">
                    <Wallet className="w-3.5 h-3.5" /> Abrechnen
                  </button>
                ) : (
                  <span className="text-[11px] font-bold text-slate-500 whitespace-nowrap">nicht abgerechnet</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
